/**
 * TLH — <tlh-fbt> « Complete the set » / « Frequently bought together » (blocks/tlh-fbt.liquid).
 *
 * - Recalcule le total selon les cases cochées et les options choisies.
 * - Mode « set » : applique en AFFICHAGE les paliers des remises automatiques Shopify
 *   (2 luminaires = −pct2 %, 3 et plus = −pct3 %), arrondis ligne par ligne comme au panier.
 * - Le bouton ajoute en une fois le produit principal (variante ET quantité choisies sur la fiche :
 *   « Single / The Pair / Set of 3+ » ou compteur natif) et les pièces cochées (x1 chacune), via le pipeline panier d'Horizon (CartLinesUpdateEvent) : le tiroir panier se
 *   met à jour et s'ouvre comme pour un ajout normal.
 * - La photo de chaque ligne suit la variante choisie, et les liens ouvrent la fiche sur cette variante
 *   (dans un nouvel onglet : réglé dans le Liquid).
 * Pare-feux : une erreur ici ne touche jamais au bouton « Add to basket » de la fiche ; en cas
 * d'échec, message clair et rien n'est ajouté en double.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

class TlhFbt extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this._busy = false;

    const cur = this.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    try {
      this._fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: cur });
    } catch (e) {
      this._fmt = { format: (n) => '£' + n.toFixed(2) };
    }

    try {
      this._main = JSON.parse(this.querySelector('[data-tlh-fbt-main]')?.textContent || '{}');
    } catch (e) {
      this._main = {};
    }

    this.addEventListener('change', () => this.update());
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-add]')) this.add();
    });

    // La variante du produit principal se choisit plus haut sur la fiche : on suit ses changements.
    this._section = this.closest('.shopify-section') || document;
    this._onPageChange = (e) => {
      if (this.contains(e.target)) return;
      clearTimeout(this._t1);
      clearTimeout(this._t2);
      this._t1 = setTimeout(() => this.update(), 60);
      this._t2 = setTimeout(() => this.update(), 500);
    };
    this._section.addEventListener('change', this._onPageChange);
    this._section.addEventListener('click', this._onPageChange);

    // Sélecteur « Compose your set » (tlh-set-picker.js) : il annonce chaque changement de quantité.
    this._onSet = () => {
      clearTimeout(this._t4);
      this._t4 = setTimeout(() => this.update(), 30);
    };
    document.addEventListener('tlh:set-change', this._onSet);

    // Re-rendu par Horizon (morph) : on recalcule.
    if ('MutationObserver' in window) {
      this._mo = new MutationObserver((mutations) => {
        if (this._busy) return;
        // Seuls de nouveaux éléments (re-rendu) comptent ; nos propres mises à jour de texte sont ignorées.
        const rerendered = mutations.some((m) => Array.from(m.addedNodes).some((node) => node.nodeType === 1));
        if (!rerendered) return;
        clearTimeout(this._t3);
        this._t3 = setTimeout(() => this.update(), 30);
      });
      this._mo.observe(this, { childList: true, subtree: true });
    }

    this.update();
  }

  disconnectedCallback() {
    this._section?.removeEventListener('change', this._onPageChange);
    this._section?.removeEventListener('click', this._onPageChange);
    document.removeEventListener('tlh:set-change', this._onSet);
    this._mo?.disconnect();
    this._ready = false;
  }

  money(cents) {
    return this._fmt.format(cents / 100);
  }

  /* Quantité du produit principal = celle du formulaire de la fiche (réglée par « Compose your set »
     ou par le compteur natif). Pare-feu : 1 si rien de lisible, 99 au maximum. */
  mainQty() {
    const idInput = this._section.querySelector('product-form-component input[name="id"]');
    const form = idInput ? idInput.form || idInput.closest('form') : null;
    const input =
      (form && form.querySelector('input[name="quantity"]')) ||
      this._section.querySelector('product-form-component input[name="quantity"]');
    const n = parseInt(input && input.value, 10);
    return Number.isFinite(n) && n > 0 ? Math.min(n, 99) : 1;
  }

  /* Remplace la photo d'une ligne par celle de la variante choisie (sans rien faire si c'est déjà la bonne) */
  swapImage(img, src) {
    if (!img || !src || img.getAttribute('src') === src) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.setAttribute('src', src);
  }

  variantUrl(base, id) {
    if (!base) return '';
    try {
      const u = new URL(base, location.origin);
      if (id) u.searchParams.set('variant', id);
      return u.pathname + u.search;
    } catch (e) {
      return base;
    }
  }

  mainVariantId() {
    const input = this._section.querySelector('product-form-component input[name="id"]');
    const id = input && input.value ? String(input.value) : '';
    if (id && this._main[id]) return id;
    const first = Object.keys(this._main).find((k) => this._main[k].a) || Object.keys(this._main)[0];
    return first || '';
  }

  lines() {
    const out = [];
    const mainId = this.mainVariantId();
    const mv = this._main[mainId];
    if (mainId && mv) out.push({ id: mainId, price: toInt(mv.p), compare: toInt(mv.c), qty: this.mainQty(), main: true, available: !!mv.a });

    this.querySelectorAll('[data-row]:not([data-main])').forEach((row) => {
      const box = row.querySelector('[data-addon]');
      const sel = row.querySelector('[data-variant]');
      if (!box || !sel) return;
      let price = 0;
      let compare = 0;
      let image = '';
      if (sel.tagName === 'SELECT') {
        const opt = sel.options[sel.selectedIndex];
        price = toInt(opt && opt.dataset.price);
        compare = toInt(opt && opt.dataset.compare);
        image = (opt && opt.dataset.image) || '';
      } else {
        price = toInt(sel.dataset.price);
        compare = toInt(sel.dataset.compare);
        image = sel.dataset.image || '';
      }
      // Photo + liens de la variante choisie
      this.swapImage(row.querySelector('.tlh-fbt__thumb img'), image);
      row.querySelectorAll('a[data-url]').forEach((a) => {
        const href = this.variantUrl(a.dataset.url, sel.value);
        if (href && a.getAttribute('href') !== href) a.setAttribute('href', href);
      });
      row.classList.toggle('is-off', !box.checked);
      const now = row.querySelector('[data-price-now]');
      const was = row.querySelector('[data-price-was]');
      if (now) now.textContent = this.money(price);
      if (was) {
        was.hidden = !(compare > price);
        if (compare > price) was.textContent = this.money(compare);
      }
      if (box.checked) out.push({ id: String(sel.value), price, compare, qty: 1, main: false, available: true });
    });
    return out;
  }

  update() {
    if (this._busy) return;
    const savings = this.dataset.savings === 'true';
    const pct2 = toInt(this.dataset.pct2) || 10;
    const pct3 = toInt(this.dataset.pct3) || 15;
    const items = this.lines();
    const n = items.reduce((s, i) => s + i.qty, 0); // nombre de luminaires (le produit principal peut être x2, x3…)

    // Ligne du produit principal (variante choisie plus haut)
    const mainId = this.mainVariantId();
    const mv = this._main[mainId];
    if (mv) {
      const q = this.mainQty();
      const t = this.querySelector('[data-main-title]');
      const pr = this.querySelector('[data-main-price]');
      const cp = this.querySelector('[data-main-compare]');
      if (t) t.textContent = mv.t;
      this.swapImage(this.querySelector('[data-main] .tlh-fbt__thumb img'), mv.i);
      if (pr) pr.textContent = this.money(toInt(mv.p) * q);
      if (cp) {
        cp.hidden = !(toInt(mv.c) > toInt(mv.p));
        if (!cp.hidden) cp.textContent = this.money(toInt(mv.c) * q);
      }
      // Ligne « Qty 2 · £149.90 each » sous le nom, seulement si la quantité choisie est 2 ou plus
      let qEl = this.querySelector('[data-main-qty]');
      const info = this.querySelector('[data-main] .tlh-fbt__info');
      if (!qEl && info && q > 1) {
        qEl = document.createElement('span');
        qEl.className = 'tlh-fbt__variant';
        qEl.setAttribute('data-main-qty', '');
        info.appendChild(qEl);
      }
      if (qEl) {
        qEl.hidden = q < 2;
        const txt = q > 1 ? `Qty ${q} · ${this.money(toInt(mv.p))} each` : '';
        if (qEl.textContent !== txt) qEl.textContent = txt;
      }
    }

    const pct = savings ? (n >= 3 ? pct3 : n === 2 ? pct2 : 0) : 0;
    // Remise calculée à l'unité puis multipliée par la quantité, comme Shopify au panier
    const full = items.reduce((s, i) => s + i.price * i.qty, 0);
    const after = items.reduce((s, i) => s + Math.round((i.price * (100 - pct)) / 100) * i.qty, 0);

    const label = this.querySelector('[data-count-label]');
    if (label) {
      const unit = savings ? (n === 1 ? (this.dataset.unit || 'light') : (this.dataset.units || 'lights')) : n === 1 ? 'item' : 'items';
      label.textContent = `Total for ${n} ${unit}`;
    }
    const tot = this.querySelector('[data-total]');
    if (tot) tot.textContent = this.money(after);
    const totFull = this.querySelector('[data-total-full]');
    if (totFull) {
      totFull.hidden = !(pct > 0);
      totFull.textContent = this.money(full);
    }
    const save = this.querySelector('[data-save]');
    if (save) {
      save.hidden = !(pct > 0);
      if (pct > 0) save.textContent = `You save ${this.money(full - after)} with ${n >= 3 ? 'Set' : 'Pair'} & save ${pct}%`;
    }

    // Badge et frise des paliers
    const badge = this.querySelector('[data-tier-badge]');
    if (badge) {
      badge.classList.toggle('is-hint', pct === 0);
      if (n >= 3) badge.textContent = `${n} ${this.dataset.units || 'lights'} · −${pct3}%`;
      else if (n === 2) badge.textContent = `2 ${this.dataset.units || 'lights'} · −${pct2}%`;
      else badge.textContent = `Add 1 more · −${pct2}%`;
    }
    const step = Math.min(n, 3);
    this.querySelectorAll('[data-step]').forEach((d) => {
      const s = toInt(d.dataset.step);
      d.classList.toggle('is-on', s <= step);
      d.classList.toggle('is-current', s === step);
    });
    this.querySelectorAll('[data-step-label]').forEach((l) => {
      l.classList.toggle('is-current', toInt(l.dataset.stepLabel) === step);
    });

    // Bouton
    const btn = this.querySelector('[data-add]');
    const btnLabel = this.querySelector('[data-add-label]');
    const mainOk = !!(mv && mv.a);
    if (btn) btn.disabled = !mainOk || n === 0;
    if (btnLabel) {
      if (!mainOk) btnLabel.textContent = 'Sold out';
      else if (n <= 1) btnLabel.textContent = `Add to basket · ${this.money(after)}`;
      else if (savings) btnLabel.textContent = `Add the set to basket · ${this.money(after)}`;
      else btnLabel.textContent = `Add ${n} items to basket · ${this.money(after)}`;
    }
  }

  showError(msg) {
    const el = this.querySelector('[data-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(this._te);
    this._te = setTimeout(() => {
      el.hidden = true;
    }, 6000);
  }

  async add() {
    if (this._busy) return;
    const items = this.lines().filter((i) => i.available);
    if (!items.length) return;

    const btn = this.querySelector('[data-add]');
    const btnLabel = this.querySelector('[data-add-label]');
    const before = btnLabel ? btnLabel.textContent : '';
    this._busy = true;
    if (btn) btn.disabled = true;
    if (btnLabel) btnLabel.textContent = 'Adding…';
    const errBox = this.querySelector('[data-error]');
    if (errBox) errBox.hidden = true;

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
    });

    const routes = (window.Theme && Theme.routes) || {};
    const addUrl = routes.cart_add_url || '/cart/add';
    const cartUrl = routes.cart_url || '/cart';
    const lines = items.map((i) => ({ id: Number(i.id), quantity: i.qty || 1 }));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise: deferred.promise,
      })
    );

    const fetchCart = () =>
      fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

    let ok = false;
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
      }).then((r) => r.json());

      if (res && res.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: res.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: res.description, errors: res.errors },
          })
        );
        const cart = await fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'tlh-fbt', sourceId: this.id || 'tlh-fbt', itemCount: lines.reduce((s, l) => s + l.quantity, 0), productId: this.dataset.productId },
        });
        this.showError(res.description || res.message || 'Sorry, something went wrong. Please try again.');
      } else {
        const cart = await fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'tlh-fbt',
            sourceId: this.id || 'tlh-fbt',
            itemCount: lines.reduce((s, l) => s + l.quantity, 0),
            productId: this.dataset.productId,
            sections: res && res.sections,
            didError: false,
          },
        });
        ok = true;
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: (error && error.message) || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      this.showError('Sorry, something went wrong. Please try again.');
    }

    if (btnLabel) btnLabel.textContent = ok ? 'Added to basket ✓' : before;
    setTimeout(
      () => {
        this._busy = false;
        this.update();
      },
      ok ? 1600 : 0
    );
  }
}

if (!customElements.get('tlh-fbt')) customElements.define('tlh-fbt', TlhFbt);
